/**
 * The command palette's "Help Center" group — client-side search over the
 * bundled help corpus.
 *
 * No server round trip: `helpCorpus` parses `virtual:help-corpus` (the repo-root
 * `docs/help/` markdown, inlined by `tools/help-corpus-plugin.ts`) at build
 * time, so the whole corpus is already in the bundle. Searching it is a string
 * scan over ~a dozen documents — cheaper than the network call it would replace,
 * and it works on the keystroke rather than 200 ms later.
 *
 * MATCHING — title AND body, deliberately.
 * Title-only would be close to useless here: the pages are named for their
 * topic ("Settings", "Integrations"), so the words a stuck user types —
 * "erasure", "passkey", "greenhouse", "fit score" — live in the BODY. So a body
 * hit counts, and the row shows the surrounding sentence so the user can tell
 * whether the page is the one they want before navigating.
 *
 * RANKING — title hits first. A body mention of "settings" should never
 * outrank the page called Settings.
 *
 * Destinations come from `internalHelpHref` / `HELP_INDEX_SLUG` in
 * `resolveHelpLink`, the same resolver the Help Center's own inter-page links
 * use — so `index` lands on `/help` and everything else on `/help/{slug}`,
 * without this module knowing that rule.
 */
import { helpDocs } from "@/help/helpCorpus";
import { HELP_INDEX_SLUG, internalHelpHref } from "@/help/resolveHelpLink";

interface HelpSearchHit {
  /** Stable id for React keys + `aria-activedescendant`. */
  id: string;
  slug: string;
  title: string;
  /** Sentence-ish excerpt around the body match; empty for a title-only hit. */
  snippet: string;
  href: string;
}

/** Characters of body text shown around a match. */
const SNIPPET_RADIUS = 60;

/**
 * Strip the markdown that would otherwise leak into a snippet as punctuation
 * noise (`## `, `**bold**`, `[label](target.md)`, backticks, list bullets).
 *
 * Intentionally shallow — this feeds a one-line preview, not a renderer. Link
 * text is kept and the target dropped, because the words a user searched for
 * are usually the label.
 */
function toPlainText(markdown: string): string {
  return markdown
    .replace(/```[\s\S]*?```/g, " ") // fenced code blocks
    .replace(/`([^`]*)`/g, "$1") // inline code
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ") // images
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1") // links → label
    .replace(/^\s{0,3}#{1,6}\s+/gm, "") // ATX headings
    .replace(/^\s*[-*+]\s+/gm, "") // list bullets
    .replace(/[*_]{1,3}/g, "") // emphasis markers
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Lower-case + strip diacritics. Same rule as the nav matcher: people type
 * ASCII, the corpus does not always contain it ("resume" must find "résumé").
 */
function normalizeForMatch(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/**
 * Build the excerpt around `matchIndex`, expanded to word boundaries and
 * ellipsised on whichever side was cut.
 */
function buildSnippet(plain: string, matchIndex: number, queryLength: number): string {
  const rawStart = Math.max(0, matchIndex - SNIPPET_RADIUS);
  const rawEnd = Math.min(plain.length, matchIndex + queryLength + SNIPPET_RADIUS);

  // Nudge to word boundaries so the excerpt does not start mid-word.
  let start = rawStart;
  if (start > 0) {
    const space = plain.indexOf(" ", start);
    if (space !== -1 && space < matchIndex) start = space + 1;
  }
  let end = rawEnd;
  if (end < plain.length) {
    const space = plain.lastIndexOf(" ", end);
    if (space !== -1 && space > matchIndex + queryLength) end = space;
  }

  const prefix = start > 0 ? "…" : "";
  const suffix = end < plain.length ? "…" : "";
  return `${prefix}${plain.slice(start, end).trim()}${suffix}`;
}

/**
 * Search the help corpus.
 *
 * @param query the raw user query
 * @param base  the tenant link base (`/{slug}`) for the resulting hrefs
 * @param limit maximum hits returned; the palette caps every group the same way
 */
export function searchHelpDocs(query: string, base: string, limit: number): HelpSearchHit[] {
  const needle = normalizeForMatch(query).trim();
  if (needle.length === 0) return [];

  const titleHits: HelpSearchHit[] = [];
  const bodyHits: HelpSearchHit[] = [];

  for (const doc of helpDocs.values()) {
    const href = internalHelpHref(base, doc.slug, "");
    const id = `help-${doc.slug}`;

    if (normalizeForMatch(doc.title).includes(needle)) {
      titleHits.push({ id, slug: doc.slug, title: doc.title, snippet: "", href });
      continue;
    }

    const plain = toPlainText(doc.body);
    const plainNorm = normalizeForMatch(plain);
    const matchIndex = plainNorm.indexOf(needle);
    if (matchIndex === -1) continue;

    // The index came from the NORMALIZED string, so slice the original only
    // when normalization was length-preserving (true for every Latin script in
    // this corpus: NFD splits an accented char into base + mark and the strip
    // removes the mark, netting zero). If some future character decomposes
    // differently the lengths diverge, and we fall back to slicing the
    // normalized text — an accent-stripped snippet, never a mis-sliced one.
    const snippetSource = plainNorm.length === plain.length ? plain : plainNorm;

    bodyHits.push({
      id,
      slug: doc.slug,
      title: doc.title,
      snippet: buildSnippet(snippetSource, matchIndex, needle.length),
      href,
    });
  }

  // Title hits first, then body hits; the `index` landing sorts last within its
  // band — it is a table of contents, so it matches broadly and helps rarely.
  const indexLast = (a: HelpSearchHit, b: HelpSearchHit): number =>
    Number(a.slug === HELP_INDEX_SLUG) - Number(b.slug === HELP_INDEX_SLUG);

  return [...titleHits.sort(indexLast), ...bodyHits.sort(indexLast)].slice(0, limit);
}
